import { useState } from "react";
import {
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ChevronDown,
  Copy,
  Inbox,
  MailQuestion,
  MailWarning,
  ShieldX,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { Recibido, ResultadoCorreo } from "../api/tipos.ts";
import { usarArchivarRecibido, usarRecibidos } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { fechaHora, haceRato, mesLargo } from "../formato.ts";
import { ErrorConsulta, EsqueletoTabla } from "../componentes/Errores.tsx";
import { CLASES_TONO, type Tono } from "../vocabulario.ts";
import { Aviso, Insignia, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

/**
 * Todo lo que llegó al buzón, y por qué entró o por qué no (SPEC-021).
 *
 * Sustituye a «Correos en revisión», que mostraba un subconjunto de esto. La
 * pantalla existe por un fallo concreto: cuando el banco cambió el subdominio
 * desde el que escribe, sus alertas se rechazaron y **no quedaba rastro en
 * ninguna parte**. No había forma de saber por qué los pagos habían dejado de
 * entrar, así que la única salida era adivinar sobre la configuración — y
 * adivinar sobre la configuración de seguridad acaba aflojándola.
 *
 * Casi todo aquí es informativo: un correo que no se supo leer se arregla
 * enseñándole el formato al parser, no confirmándolo a mano. La única acción es
 * archivar (SPEC-024), y no afirma lo contrario: no dice «esto es un pago»,
 * dice «esto no lo es». El banco manda avisos que no son movimientos, y si se
 * quedan en la bandeja para siempre la bandeja se vuelve ruido y deja de
 * mirarse — que es exactamente la forma de no ver el correo que sí importaba.
 */

interface Aspecto {
  etiqueta: string;
  icono: LucideIcon;
  tono: Tono;
  explica: string;
}

const ASPECTO: Record<ResultadoCorreo, Aspecto> = {
  REGISTRADO: {
    etiqueta: "Entró",
    icono: CheckCircle2,
    tono: "exito",
    explica: "Se leyó y creó su movimiento.",
  },
  EN_REVISION: {
    etiqueta: "No se supo leer",
    icono: MailQuestion,
    tono: "aviso",
    explica:
      "Viene de una fuente registrada, pero su formato no coincide con ningún patrón conocido. " +
      "No se registró ningún pago: 4drando prefiere apartarlo a inventarse uno.",
  },
  RECHAZADO: {
    etiqueta: "Ni se abrió",
    icono: ShieldX,
    tono: "peligro",
    explica:
      "El remitente no es una fuente registrada ni un buzón autorizado a reenviar. Si esperabas " +
      "este pago, aquí está la dirección que hay que registrar.",
  },
  DUPLICADO: {
    etiqueta: "Repetido",
    icono: Copy,
    tono: "neutro",
    explica: "Ese mismo correo ya se había procesado. No se cuenta dos veces.",
  },
};

/**
 * Hay dos formas de ser repetido y no dicen lo mismo.
 *
 * Una es el mismo correo dos veces —el proveedor reintentó—. La otra es el
 * mismo PAGO por dos caminos: el banco escribiendo directo y un buzón tuyo
 * reenviando lo mismo. En la segunda no hay ningún correo repetido, y decir que
 * sí despistaría a quien viene justo a entender por qué falta un movimiento.
 */
const EXPLICA_MOTIVO: Record<string, string> = {
  MISMO_PAGO_OTRA_RUTA:
    "Este pago ya había entrado por el otro camino. No es un correo repetido: son dos correos " +
    "distintos que describen el mismo movimiento, así que solo se contó una vez.",
};

const FILTROS: { valor: ResultadoCorreo | ""; texto: string }[] = [
  { valor: "", texto: "Todo" },
  { valor: "REGISTRADO", texto: "Entraron" },
  { valor: "EN_REVISION", texto: "Sin leer" },
  { valor: "RECHAZADO", texto: "Rechazados" },
  { valor: "DUPLICADO", texto: "Repetidos" },
];

export function Recibidos() {
  const [filtro, setFiltro] = useState<ResultadoCorreo | "">("");
  const [archivados, setArchivados] = useState(false);
  const { data, isPending, error, refetch } = usarRecibidos(filtro, archivados);
  const correos = data?.correos ?? [];

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  // Un tope que se aplica en silencio es peor que no tenerlo: la lista se vería
  // corta y se concluiría que no llegó nada más (SPEC-021 §4).
  const topeLleno = !!data && data.rechazados24h >= data.tope;

  return (
    <Seccion
      titulo="Recibidos"
      descripcion="Todo lo que llegó al buzón de esta cuenta, y qué se hizo con cada correo."
    >
      <div className="flex flex-wrap gap-2">
        {FILTROS.map((f) => (
          <button
            key={f.valor}
            onClick={() => setFiltro(f.valor)}
            aria-pressed={filtro === f.valor}
            className={cx(
              "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
              filtro === f.valor
                ? "border-marca bg-marca-suave text-marca-tinta"
                : "border-borde text-tenue hover:bg-superficie-2 hover:text-tinta",
            )}
          >
            {f.texto}
          </button>
        ))}

        {/* El contador va SIEMPRE, incluso en cero. Un montón de correos
            escondidos detrás de un filtro que no se ve es un archivo que se
            traga cosas; verlo escrito es lo que mantiene el archivar reversible. */}
        <button
          onClick={() => setArchivados((v) => !v)}
          aria-pressed={archivados}
          className={cx(
            "ml-auto inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
            archivados
              ? "border-marca bg-marca-suave text-marca-tinta"
              : "border-borde text-tenue hover:bg-superficie-2 hover:text-tinta",
          )}
        >
          <Archive className="size-3.5" aria-hidden />
          Archivados
          {data ? <span className="cifra">({data.archivados})</span> : null}
        </button>
      </div>

      {/* La serie solo aparece mirando el archivo: es donde está la persona
          que puede hacer algo con ella. Sin este número, el día que sean
          cuarenta al mes nadie lo nota — se archivan de uno en uno, y de uno en
          uno nada duele lo bastante para acordarse del mes anterior. */}
      {archivados && !!data?.archivadosPorMes.length && (
        <p className="max-w-prose text-sm text-tenue">
          Archivados por mes:{" "}
          {data.archivadosPorMes.map((m, i) => (
            <span key={m.mes}>
              {i > 0 && " · "}
              <span className="cifra">{m.n}</span> en {mesLargo(m.mes)}
            </span>
          ))}
          . Si el mismo aviso vuelve cada mes, esta línea es donde se va a ver.
        </p>
      )}

      {topeLleno && (
        <Aviso tono="riesgo" titulo="Puede haber más correos rechazados sin anotar" icono={MailWarning}>
          Se alcanzó el máximo de {data.tope} rechazos anotados en 24 horas. El tope existe porque
          la dirección de entrada es pública: sin él, cualquiera que la conozca podría llenar esta
          pantalla. Lo que ves es una muestra, no la lista completa.
        </Aviso>
      )}

      {isPending ? (
        <Tarjeta className="p-4">
          <EsqueletoTabla filas={4} />
        </Tarjeta>
      ) : correos.length === 0 ? (
        <Tarjeta>
          <Vacio icono={archivados ? Archive : Inbox} titulo="Nada que mostrar">
            {archivados
              ? "No has archivado ningún correo."
              : filtro
                ? "Ningún correo con ese resultado."
                : "Todavía no ha llegado ningún correo."}
          </Vacio>
        </Tarjeta>
      ) : (
        <ul className="space-y-3">
          {correos.map((c) => (
            <Correo key={c.id} correo={c} />
          ))}
        </ul>
      )}
    </Seccion>
  );
}

function Correo({ correo }: { correo: Recibido }) {
  const [abierto, setAbierto] = useState(false);
  const { puedo } = usarSesion();
  const archivar = usarArchivarRecibido();
  const a = ASPECTO[correo.resultado] ?? ASPECTO.EN_REVISION;
  const Icono = a.icono;
  const estaArchivado = !!correo.archivado_en;

  return (
    <li>
      <Tarjeta className="overflow-hidden">
        <button
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="flex w-full items-start gap-4 p-4 text-left transition-colors hover:bg-superficie-2"
        >
          <span
            className={cx(
              "grid size-9 shrink-0 place-items-center rounded-xl",
              CLASES_TONO[a.tono].fondo,
              CLASES_TONO[a.tono].texto,
            )}
          >
            <Icono className="size-4.5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1 space-y-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium">{correo.asunto || "(sin asunto)"}</span>
              <Insignia tono={a.tono}>{a.etiqueta}</Insignia>
              {correo.procedencia === "REENVIADA" && <Insignia tono="neutro">reenviado</Insignia>}
              {estaArchivado && <Insignia tono="neutro">archivado</Insignia>}
            </span>
            <span className="block truncate text-sm text-tenue">{correo.remitente}</span>
            {/* La hora de recepción, no solo "hace un rato": con dos pagos del
                mismo monto el mismo día, es lo único que los distingue. */}
            <span className="block text-xs text-tenue-2">
              {fechaHora(correo.recibido_en)} · {haceRato(correo.recibido_en)}
            </span>
          </span>
          <ChevronDown
            className={cx(
              "size-4 shrink-0 text-tenue-2 transition-transform",
              abierto && "rotate-180",
            )}
            aria-hidden
          />
        </button>

        {abierto && (
          <div className="space-y-3 border-t border-borde bg-superficie-2/50 p-4 aparecer">
            <p className="text-sm leading-relaxed text-tenue">
              <span className="font-medium text-tinta">{a.etiqueta}. </span>
              {(correo.motivo && EXPLICA_MOTIVO[correo.motivo]) || a.explica}
            </p>

            {/* Texto plano, nunca HTML: en un rechazo, parte de esto lo escribió
                un desconocido. */}
            {correo.detalle && (
              <p className="rounded-xl border border-borde bg-superficie p-3 text-sm text-tenue">
                {correo.detalle}
              </p>
            )}

            {correo.cuerpo ? (
              <pre className="max-h-72 overflow-auto rounded-xl border border-borde bg-superficie p-3 text-xs leading-relaxed whitespace-pre-wrap text-tenue">
                {correo.cuerpo}
              </pre>
            ) : (
              correo.resultado === "RECHAZADO" && (
                <p className="text-xs text-tenue-2">
                  De un remitente no autorizado no se guarda el cuerpo: es lo único que él controla
                  por completo.
                </p>
              )
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-tenue-2">
              {correo.fuente && <span>Fuente: {correo.fuente}</span>}
              {correo.patron && <span>Patrón: {correo.patron}</span>}
              {correo.message_id && <span className="cifra">ID: {correo.message_id}</span>}
              {estaArchivado && correo.archivado_en && (
                <span>
                  Archivado por {correo.archivado_por} · {fechaHora(correo.archivado_en)}
                </span>
              )}
            </div>

            {/* Dentro del desplegable a propósito: para archivarlo hay que
                haberlo abierto, y abrirlo enseña el cuerpo. Un botón en la lista
                se pulsa sin leer, y aquí lo que se afirma es «esto no es un pago». */}
            {puedo(PERMISO.DECIDIR) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-borde pt-3">
                <button
                  onClick={() =>
                    archivar.mutate({ id: Number(correo.id), archivar: !estaArchivado })
                  }
                  disabled={archivar.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-borde px-3 py-1.5 text-sm font-medium text-tenue transition-colors hover:bg-superficie hover:text-tinta disabled:opacity-50"
                >
                  {estaArchivado ? (
                    <ArchiveRestore className="size-4" aria-hidden />
                  ) : (
                    <Archive className="size-4" aria-hidden />
                  )}
                  {estaArchivado ? "Devolver a la bandeja" : "Esto no es un pago"}
                </button>
                <p className="text-xs text-tenue-2">
                  {estaArchivado
                    ? "Vuelve a la lista tal como estaba."
                    : "Lo saca de la bandeja. No se borra nada y puedes devolverlo."}
                </p>
              </div>
            )}
          </div>
        )}
      </Tarjeta>
    </li>
  );
}
